import { genModelViewMatrices, ICamera, ICameraPos, updateCamera } from "./Camera";
import { drawAllArrows } from "./components/Arrow";
import { drawBlockLabels } from "./components/SectionLabels";
import { drawModelCard } from "./components/ModelCard";
import { IGptModelLink, IGpuGptModel, IModelShape } from "./GptModel";
import { genGptModelLayout, IBlkDef, IGptModelLayout } from "./GptModelLayout";
import { drawText, IFontAtlasData, IFontOpts, measureText } from "./render/fontRender";
import { initRender, IRenderState, IRenderView, renderModel, resetRenderBuffers } from "./render/modelRender";
import { beginQueryAndGetPrevMs, endQuery } from "./render/queryManager";
import { SavedState } from "./SavedState";
import { isNotNil } from "@/src/utils/data";
import { Vec3, Vec4 } from "@/src/utils/vector";
import { initWalkthrough, runWalkthrough } from "./walkthrough/Walkthrough";
import { IColorMix } from "./Annotations";
import { Mat4f } from "@/src/utils/matrix";
import { runMouseHitTesting } from "./Interaction";
import { RenderPhase } from "./render/sharedRender";
import { drawBlockInfo } from "./components/BlockInfo";
import { NativeFunctions } from "./NativeBindings";
import { IWasmGptModel, stepWasmModel, syncWasmDataWithJsAndGpu } from "./GptModelWasm";
import { IMovementInfo, manageMovement } from "./components/MovementControls";
import { IBlockRender, initBlockRender } from "./render/blockRender";
import { addLine } from "./render/lineRender";
import { ILayout } from "../utils/layout";
import { DimStyle } from "./walkthrough/WalkthroughTools";
import { Subscriptions } from "../utils/hooks";

export interface IProgramState {
    native: NativeFunctions | null;
    wasmGptModel: IWasmGptModel | null;
    stepModel: boolean;
    mouse: IMouseState;
    render: IRenderState;
    inWalkthrough: boolean;
    walkthrough: ReturnType<typeof initWalkthrough>;
    camera: ICamera;
    htmlSubs: Subscriptions;
    layout: IGptModelLayout;
    mainExample: IModelExample;
    examples: IModelExample[];
    currExampleId: number;
    shape: IModelShape;
    gptGpuModel: IGpuGptModel | null;
    jsGptModel: IGptModelLink | null;
    movement: IMovementInfo;
    display: IDisplayState;
    pageLayout: ILayout;
    conciseLayerView: boolean;
    markDirty: () => void;
}

export interface IModelExample {
    name: string;
    shape: IModelShape;
    enabled: boolean;
    layout?: IGptModelLayout;
    blockRender: IBlockRender;
    offset: Vec3;
    modelCardOffset: Vec3;
    camera?: ICameraPos;
}

export interface IMouseState {
    mousePos: Vec3;
}

export interface IDisplayState {
    tokenColors: IColorMix | null;
    tokenIdxColors: IColorMix | null;
    tokenOutputColors: IColorMix | null;
    tokenIdxModelOpacity?: number[];
    topOutputOpacity?: number;
    lines: string[];
    hoverTarget: IHoverTarget | null;
    blkIdxHover: number[] | null;
    dimHover: DimStyle | null;
}

export interface IHoverTarget {
    subCube: IBlkDef;
    mainCube: IBlkDef;
    mainIdx: Vec3;
}

export function initProgramState(canvasEl: HTMLCanvasElement, fontAtlasData: IFontAtlasData): IProgramState {

    let render = initRender(canvasEl, fontAtlasData);
    let walkthrough = initWalkthrough();

    // `?view=layer` is the concise companion to the full topology. It draws
    // one compact, representative Transformer block rather than the whole
    // 64-layer axis. Both modes retain the published Qwen config on the model
    // card; visual cells are deliberately compact display axes, not weights.
    let conciseLayerView = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('view') === 'layer';
    let shape: IModelShape = {
        B: 1,
        T: conciseLayerView ? 6 : 11,
        C: conciseLayerView ? 24 : 48,
        nHeads: conciseLayerView ? 1 : 3,
        A: conciseLayerView ? 24 : 16,
        nBlocks: conciseLayerView ? 1 : 64,
        vocabSize: 3,
    };
    let qwenLayout = genGptModelLayout(shape);
    let camera: ICamera = {
        angle: new Vec3(296, 16, conciseLayerView ? 1.65 : 9),
        center: new Vec3(0, conciseLayerView ? 86 : 300, 0),
        transition: {},
        modelMtx: new Mat4f(),
        viewMtx: new Mat4f(),
        lookAtMtx: new Mat4f(),
        camPos: new Vec3(),
        camPosModel: new Vec3(),
    }

    let gpt2ShapeSmall: IModelShape = {
        B: 1,
        T: 1024,
        C: 768,
        nHeads: 12,
        A: 768 / 12,
        nBlocks: 12,
        vocabSize: 50257,
    };

    let gpt2ShapeLarge: IModelShape = {
        B: 1,
        T: 1024,
        C: 1600,
        nHeads: 25,
        A: 1600 / 25,
        nBlocks: 48,
        vocabSize: 50257,
    };

    let gpt3Shape: IModelShape = {
        B: 1,
        T: 1024,
        C: 12288,
        nHeads: 96,
        A: 12288 / 96,
        nBlocks: 96,
        vocabSize: 50257,
    };

    function makeCamera(center: Vec3, angle: Vec3): ICameraPos {
        return { center, angle };
    }

    let delta = new Vec3(10000, 0, 0);

    return {
        native: null,
        wasmGptModel: null,
        render: render!,
        inWalkthrough: false,
        walkthrough,
        camera,
        shape: shape,
        layout: genGptModelLayout(shape),
        currExampleId: -1,
        mainExample: {
            name: 'Qwen3.8-27B',
            enabled: true,
            shape: shape,
            offset: new Vec3(),
            modelCardOffset: new Vec3(),
            blockRender: null!,
            camera: { center: camera.center, angle: camera.angle },
        },
        examples: [],
        gptGpuModel: null,
        jsGptModel: null,
        stepModel: false,
        conciseLayerView,
        markDirty: () => { },
        htmlSubs: new Subscriptions(),
        mouse: {
            mousePos: new Vec3(),
        },
        movement: {
            action: null,
            actionHover: null,
            target: [0, 0],
            depth: 1,
            cameraLerp: null,
         },
        display: {
            tokenColors: null,
            tokenIdxColors: null,
            tokenOutputColors: null,
            lines: [],
            hoverTarget: null,
            dimHover: null,
            blkIdxHover: null,
        },
        pageLayout: {
            height: 0,
            width: 0,
            isDesktop: true,
            isPhone: true,
        }
    };
}

function applyConciseLayerLayout(layout: IGptModelLayout) {
    let block = layout.blocks[0];
    if (!block) {
        return;
    }

    // In concise mode this is intentionally an architecture thumbnail, not a
    // shrunken 64-layer graph: keep six semantic tensors from one block and
    // reposition them as a short attention → FFN residual path.
    for (let cube of layout.cubes) {
        cube.opacity = 0.0;
        cube.highlight = 0.0;
    }

    type Placement = [IBlkDef, number, number, number, number];
    let qWeight = block.heads[0]?.qWeightBlock ?? block.projWeight;
    let cubes: Placement[] = [
        [block.ln1.lnResid, -42, 0, 34, 18],
        [qWeight, 12, 0, 52, 18],
        [block.attnResidual, -15, 48, 52, 18],
        [block.ln2.lnResid, -42, 98, 34, 18],
        [block.mlpAct, 12, 98, 52, 18],
        [block.mlpResidual, -15, 146, 52, 18],
    ];

    for (let [cube, x, y, dx, dy] of cubes) {
        cube.x = x;
        cube.y = y;
        cube.z = -4;
        cube.dx = dx;
        cube.dy = dy;
        cube.dz = 8;
        cube.cx = 8;
        cube.cy = 6;
        cube.cz = 2;
        cube.opacity = 1.0;
    }

    layout.height = 176;
}

function drawConciseLayerWiring(state: IProgramState) {
    let block = state.layout.blocks[0];
    if (!block) {
        return;
    }

    // Two compact compute paths plus their residual bypasses. They are drawn
    // in front of the tensors so the concise 3D view keeps a readable flow.
    let qWeight = block.heads[0]?.qWeightBlock ?? block.projWeight;
    let ln1 = block.ln1.lnResid;
    let attnResidual = block.attnResidual;
    let ln2 = block.ln2.lnResid;
    let mlp = block.mlpAct;
    let mlpResidual = block.mlpResidual;
    let z = 7;
    let green = Vec4.fromHexColor('#8ed19b');
    let blue = Vec4.fromHexColor('#9190ec');
    let muted = Vec4.fromHexColor('#7b8170');
    let normal = new Vec3(0, 0, 1);
    let line = (a: Vec3, b: Vec3, color: Vec4, thick = 2.4) => addLine(state.render.lineRender, thick, color, a, b, normal);

    // RMSNorm → attention → attention residual.
    line(new Vec3(ln1.x + ln1.dx, ln1.y + ln1.dy / 2, z), new Vec3(qWeight.x, qWeight.y + qWeight.dy / 2, z), blue);
    line(new Vec3(qWeight.x + qWeight.dx / 2, qWeight.y + qWeight.dy, z), new Vec3(attnResidual.x + attnResidual.dx / 2, attnResidual.y, z), green);
    // First residual bypass around attention.
    line(new Vec3(ln1.x + ln1.dx / 2, ln1.y + ln1.dy, z), new Vec3(-66, ln1.y + ln1.dy, z), muted, 1.8);
    line(new Vec3(-66, ln1.y + ln1.dy, z), new Vec3(-66, attnResidual.y + attnResidual.dy / 2, z), muted, 1.8);
    line(new Vec3(-66, attnResidual.y + attnResidual.dy / 2, z), new Vec3(attnResidual.x, attnResidual.y + attnResidual.dy / 2, z), muted, 1.8);

    // Attention residual → RMSNorm → SwiGLU → MLP residual.
    line(new Vec3(attnResidual.x + attnResidual.dx / 2, attnResidual.y + attnResidual.dy, z), new Vec3(ln2.x + ln2.dx / 2, ln2.y, z), green);
    line(new Vec3(ln2.x + ln2.dx, ln2.y + ln2.dy / 2, z), new Vec3(mlp.x, mlp.y + mlp.dy / 2, z), blue);
    line(new Vec3(mlp.x + mlp.dx / 2, mlp.y + mlp.dy, z), new Vec3(mlpResidual.x + mlpResidual.dx / 2, mlpResidual.y, z), green);
    // Second residual bypass around the feed-forward path.
    line(new Vec3(attnResidual.x + attnResidual.dx / 2, attnResidual.y + attnResidual.dy, z), new Vec3(-66, attnResidual.y + attnResidual.dy, z), muted, 1.8);
    line(new Vec3(-66, attnResidual.y + attnResidual.dy, z), new Vec3(-66, mlpResidual.y + mlpResidual.dy / 2, z), muted, 1.8);
    line(new Vec3(-66, mlpResidual.y + mlpResidual.dy / 2, z), new Vec3(mlpResidual.x, mlpResidual.y + mlpResidual.dy / 2, z), muted, 1.8);
}

export function runProgram(view: IRenderView, state: IProgramState) {
    let timer0 = performance.now();

    if (!state.render) {
        return;
    }

    resetRenderBuffers(state.render);
    state.render.sharedRender.activePhase = RenderPhase.Opaque;
    state.display.lines = [];
    state.display.hoverTarget = null;
    state.display.tokenColors = null;
    state.display.tokenIdxColors = null;

    if (state.wasmGptModel && state.jsGptModel) {
        syncWasmDataWithJsAndGpu(state.wasmGptModel, state.jsGptModel);
    }

    if (state.stepModel && state.wasmGptModel && state.jsGptModel) {
        state.stepModel = false;
        stepWasmModel(state.wasmGptModel, state.jsGptModel);
    }

    // generate the base model, incorporating the gpu-side model if available
    state.layout = genGptModelLayout(state.shape, state.jsGptModel);
    state.layout.weightCount = 27000000000;
    if (state.conciseLayerView) {
        applyConciseLayerLayout(state.layout);
    }

    // @TODO: handle different models in the same scene.
    // Maybe need to copy a lot of different things like the entire render state per model?
    for (let example of state.examples) {
        if (example.enabled && !example.layout) {
            let layout = genGptModelLayout(example.shape, null, example.offset);
            example.layout = layout;
        }
    }

    genModelViewMatrices(state, state.layout!);

    let queryRes = beginQueryAndGetPrevMs(state.render.queryManager, 'render');
    if (isNotNil(queryRes)) {
        state.render.lastGpuMs = queryRes;
    }

    state.render.renderTiming = false; // state.pageLayout.isDesktop;

    // will modify layout; view; render a few things.
    if (state.inWalkthrough) {
        runWalkthrough(state, view);
    }

    updateCamera(state, view);

    drawBlockInfo(state);
    // The concise companion is a compact single-layer architecture glyph;
    // avoid drawing the full detailed arrow graph or the distant model card.
    if (!state.conciseLayerView) {
        drawAllArrows(state.render, state.layout);
        drawModelCard(state, state.layout, 'Qwen3.8-27B', new Vec3());
    } else {
        drawConciseLayerWiring(state);
    }
    // drawTokens(state.render, state.layout, state.display);

    for (let example of state.examples) {
        if (example.enabled && example.layout) {
            drawModelCard(state, example.layout, example.name, example.offset.add(example.modelCardOffset));
        }
    }

    // manageMovement(state, view);
    runMouseHitTesting(state);
    state.render.sharedRender.activePhase = RenderPhase.Opaque;
    drawBlockLabels(state.render, state.layout);

    let lineNo = 1;
    let tw = state.render.size.x;
    state.render.sharedRender.activePhase = RenderPhase.Overlay2D;
    for (let line of state.display.lines) {
        let opts: IFontOpts = { color: new Vec4(), size: 14 };
        let w = measureText(state.render.modelFontBuf, line, opts);
        drawText(state.render.modelFontBuf, line, tw - w - 4, lineNo * opts.size * 1.3 + 4, opts)
        lineNo++;
    }

    // render everything; i.e. here's where we actually do gl draw calls
    // up until now, we've just been putting data in cpu-side buffers
    renderModel(state);

    endQuery(state.render.queryManager, 'render');
    state.render.gl.flush();

    state.render.lastJsMs = performance.now() - timer0;
}
